import { fetchPutResponse } from "./ApiUtilities";
import { getAuthenticatedWebSocketUrl, getUserId } from "./authHelper";
import { getChatSuggestionsFromPayload } from "./chatSuggestionUtilities";

const EMPTY_RESPONSE_RETRY_LIMIT = 1;
const EMPTY_RESPONSE_FALLBACK_TEXT =
  "No visible response was received. Please try again.";
// Opens an analysis block the moment a question is sent; the socket's own INFO
// steps stream in underneath it and the block is closed as usual (same as the
// CoTrader assistant).
const PROCESSING_PLACEHOLDER = "<CollapsibleDiv>Processing\n";

const formatDateOnly = (dateValue) =>
  `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(dateValue.getDate()).padStart(2, "0")}`;

const buildMonitorSelection = (
  selectedPoint,
  selectedDate,
  pointClickNewsLookbackDays
) => {
  if (!selectedPoint || !selectedDate) {
    return {};
  }

  const normalizedDate =
    selectedDate instanceof Date ? selectedDate : new Date(selectedDate);

  if (Number.isNaN(normalizedDate.getTime())) {
    return {};
  }

  const selectedDateOnly = formatDateOnly(normalizedDate);
  const hasPointClickNewsLookbackDays =
    Number.isFinite(pointClickNewsLookbackDays) &&
    pointClickNewsLookbackDays >= 0;
  const selectedDatePayload = hasPointClickNewsLookbackDays
    ? (() => {
        const startDate = new Date(normalizedDate);
        startDate.setDate(
          startDate.getDate() - Number(pointClickNewsLookbackDays)
        );
        return {
          selected_date_range: {
            start_date: formatDateOnly(startDate),
            end_date: selectedDateOnly,
          },
        };
      })()
    : {
        selected_date: selectedDateOnly,
      };

  return {
    ...selectedDatePayload,
    point_name: selectedPoint?.name || null,
    point_country: selectedPoint?.country || null,
    point_reference_id:
      selectedPoint?.reference_id || selectedPoint?.isin || null,
  };
};

export const fetchDocumentIds = async (
  isin,
  setDocumentIds,
  dateFilter,
  setInUseDocumentIds
) => {
  var requestBody = null;
  try {
    const category_theme = isin;
    const start_date = dateFilter.startDate || new Date().toISOString();
    const end_date = dateFilter.endDate || new Date().toISOString();
    const publication_timestamp = [start_date, end_date];

    requestBody = {
      category_theme,
      publication_timestamp,
    };
    const response = await fetchPutResponse(
      `document:filter?orderType=DESC&orderBy=publication_timestamp&offset=0&limit=100&getCount=false`,
      requestBody
    );
    const data = await response.json();
    const document_ids = data.results.records.map((x) => {
      return {
        doc_id: x.id,
        time_stamp: x.publication_timestamp,
        category_theme: x.category_theme,
        public_data_source_url: x.public_data_source_url,
      };
    });

    setInUseDocumentIds(document_ids);
    setDocumentIds(document_ids);
  } catch (error) {
    console.error("Error in fetching Document Ids:", error);
  } finally {
  }
};

export const askQuestion = async (
  setIsLoading,
  userInput,
  setUserInput,
  documentIdInput,
  inUseDocumentIds,
  setMessages,
  messages,
  setPageNumbers,
  getPageNumber,
  dashboardName,
  filterCountries,
  filterCompanies,
  reference_ids,
  searchFlag,
  isStoppedRef,
  setIsStopped,
  sources,
  default_embedding,
  setDynamicSuggestions,
  showSuggestions,
  wsRef,
  selectedNews,
  newsList,
  setIsStreaming,
  ragRatioReq,
  // tabs,
  // setTabs,
  // setTvTickers,
  selectedPoint,
  selectedDate,
  pointClickNewsLookbackDays,
  summary,
  summaryVersion,
  setSummary,
  setSummaryVersion,
  retryState = null
) => {
  const {
    silentRetry = false,
    retryCount = 0,
    preservedContext = null,
    userInputOverride = null,
    evidenceRefs = [],
    requestMode = null,
    ticker = "",
    slug = "",
  } = retryState || {};
  try {
    // =========================
    // Early exits & setup
    // =========================
    setDynamicSuggestions([]);
    const normalizedUserInput =
      typeof userInputOverride === "string" ? userInputOverride : userInput;
    if (!normalizedUserInput.trim()) return;
    const normalizedEvidenceRefs = Array.isArray(evidenceRefs)
      ? evidenceRefs
      : [];

    const document_ids = documentIdInput.trim()
      ? [documentIdInput]
      : inUseDocumentIds.map((x) => x.doc_id);

    setIsLoading(true);
    if (!silentRetry) {
      setUserInput("");
    }

    const newMessage = { type: "question", text: normalizedUserInput };

    // Append user's question
    if (!silentRetry) {
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    }

    // Build short context (last 4 Q/A only)
    const lastQAContext =
      typeof preservedContext === "string"
        ? preservedContext
        : messages
            .filter((msg) => msg.type === "question" || msg.type === "answer")
            .slice(-4)
            .map(
              (msg) =>
                `${msg.type === "question" ? "Question:" : "Answer:"} ${msg.text}`
            )
            .join(" ");

    // Seed the answer with an already-open analysis block so the header and
    // its timer render on send instead of waiting for the first INFO frame.
    // We only mutate THIS answer afterwards.
    if (!silentRetry) {
      setMessages((prev) => [
        ...prev,
        { type: "answer", text: PROCESSING_PLACEHOLDER },
      ]);
    } else {
      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i -= 1) {
          if (updated[i]?.type === "answer") {
            updated[i] = { type: "answer", text: PROCESSING_PLACEHOLDER };
            break;
          }
        }
        return updated;
      });
    }

    const random_number = Math.floor(100000 + Math.random() * 900000);
    const isSummarizeGptRequest = requestMode === "SUMMARIZEGPT";
    const chat_id = isSummarizeGptRequest
      ? `SUMMARIZEGPT-${Date.now()}-${random_number}`
      : `EMGPT-${getUserId()}-${random_number}`;
    const user_id = getUserId();

    let document_ctx = false;

    if (selectedNews) {
      document_ctx = selectedNews.summary;
    } else if (selectedPoint && newsList.length < 10) {
      document_ctx = newsList.map((item) => item.summary).join("\n\n\n");
    }


    const hasPrivateCompanySlug =
      typeof slug === "string" && Boolean(slug.trim());
    const normalEmgptTarget = hasPrivateCompanySlug
      ? { slug: slug.trim() }
      : typeof ticker === "string" && ticker.trim()
        ? { ticker: ticker.trim() }
        : {};
    const requestBody = isSummarizeGptRequest
      ? hasPrivateCompanySlug
        ? {
            query: newMessage.text,
            context: "",
            timestamp: new Date().toISOString(),
            chat_id,
            sender: user_id,
            sender_display_name: "USER",
            slug: slug.trim(),
            origin: "SUMMARIZEGPT",
            summary: summary || "",
            summary_version: summaryVersion || 0,
          }
        : {
            query: newMessage.text,
            ticker: typeof ticker === "string" ? ticker.trim() : "",
            chat_id,
            origin: "SUMMARIZEGPT",
          }
      : {
          query: `question: ${newMessage.text}`,
          context: lastQAContext,
          document_id: document_ids,
          dashboard_name: dashboardName,
          countries: filterCountries,
          company_ids: filterCompanies,
          ...normalEmgptTarget,
          reference_ids,
          evidence_refs: normalizedEvidenceRefs,
          timestamp: new Date().toISOString(),
          chat_id,
          sender: user_id,
          sender_display_name: "USER",
          search_flag: searchFlag,
          // show_messages: true,

          sources: sources
            ? sources
                .filter((x) => x.value !== "NEWS" && x.value !== "SENTIMENT")
                .map((x) => x.value)
            : [],
          embedding_model: default_embedding[0]?.trim() || "text-embedding-ada-002",
          origin: "EMGPT",
          show_suggestions: showSuggestions,
          document_context: document_ctx,
          monitor_selection: buildMonitorSelection(
            selectedPoint,
            selectedDate,
            pointClickNewsLookbackDays
          ),
          evidence_scope: selectedPoint ? "selected_articles_only" : null,
          rag_ratio:  (ragRatioReq || selectedPoint ? 1.0 : null) ,
          summary: summary || "",
          summary_version: summaryVersion || 0,
        };

    // =========================
    // NEW: frame-batched rendering
    // =========================
    let rafId = null;
    let streamBuf = ""; // text accumulated for the CURRENT frame

    const flushAnswerOncePerFrame = () => {
      if (!streamBuf) return;
      const chunk = streamBuf;
      streamBuf = "";
      // Mutate ONLY the last answer message
      setMessages((prev) => {
        if (!prev.length) return prev;
        const updated = [...prev];
        // Find last "answer" from the end (robust if other messages are added)
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i]?.type === "answer") {
            const prevText = updated[i].text || "";
            updated[i] = { ...updated[i], text: prevText + chunk };
            break;
          }
        }
        return updated;
      });
    };

    const scheduleFrameFlush = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        flushAnswerOncePerFrame();
      });
    };

    const enqueueText = (s) => {
      if (!s) return;
      streamBuf += s;
      scheduleFrameFlush();
    };

    // =========================
    // NEW: 25ms WS burst coalescer
    // =========================
    let wsBurstTimer = null;
    let wsBurstBuf = "";

    const addToBurst = (s) => {
      if (!s) return;
      wsBurstBuf += s;
      if (wsBurstTimer) return;
      wsBurstTimer = setTimeout(() => {
        enqueueText(wsBurstBuf);
        wsBurstBuf = "";
        wsBurstTimer = null;
      }, 25);
    };

    // =========================
    // WS lifecycle & streaming
    // =========================
    let reconnectEnabled = true;
    let hasFinished = false;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    let lastType = null; // to auto-close/open chart/info wrappers
    let activeAssistantMessageId = null;
    let hasRenderableContent = false;
    // The seeded placeholder already opened one.
    let collapsibleOpen = true;

    const closeCollapsibleIfOpen = () => {
      if (!collapsibleOpen) return;
      collapsibleOpen = false;
      addToBurst("</CollapsibleDiv>");
    };

    // Sources stay as data on the message instead of being spliced into its
    // markup; the renderer decides where they land.
    const addSourceUrls = (message) => {
      const incoming = String(message)
        .split(" ")
        .filter((url) => url);
      if (!incoming.length) return;

      setMessages((prev) => {
        if (!prev.length) return prev;
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i -= 1) {
          if (updated[i]?.type === "answer") {
            const merged = [...(updated[i].sources || [])];
            incoming.forEach((url) => {
              if (!merged.includes(url)) merged.push(url);
            });
            updated[i] = { ...updated[i], sources: merged };
            break;
          }
        }
        return updated;
      });
    };

    const applyEmptyResponseFallback = () => {
      setMessages((prev) => {
        if (!prev.length) return prev;
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i -= 1) {
          if (updated[i]?.type === "answer") {
            // The seeded analysis block is not an answer on its own.
            const currentText = (updated[i]?.text || "")
              .replace(/<\/?collapsiblediv>/gi, "")
              .replace(/^Processing\s*/, "");
            if (currentText.trim()) {
              return prev;
            }
            updated[i] = {
              ...updated[i],
              text: EMPTY_RESPONSE_FALLBACK_TEXT,
            };
            return updated;
          }
        }
        return prev;
      });
    };

    const resetReplayState = () => {
      if (wsBurstTimer) {
        clearTimeout(wsBurstTimer);
        wsBurstTimer = null;
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      wsBurstBuf = "";
      streamBuf = "";
      lastType = null;
      // The server replays the answer from the start; reopen the block.
      collapsibleOpen = true;
      setIsStreaming?.(false);
      setMessages((prev) => {
        if (!prev.length) return prev;
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i -= 1) {
          if (updated[i]?.type === "answer") {
            updated[i] = {
              ...updated[i],
              text: PROCESSING_PLACEHOLDER,
              sources: [],
            };
            return updated;
          }
        }
        return prev;
      });
    };

    const stopAll = () => {
      reconnectEnabled = false;
      try {
        wsRef.current?.close();
      } catch {}
      closeCollapsibleIfOpen();
      if (wsBurstTimer) {
        clearTimeout(wsBurstTimer);
        wsBurstTimer = null;
      }
      if (wsBurstBuf) {
        enqueueText(wsBurstBuf);
        wsBurstBuf = "";
      }
      // ensure any pending text is painted
      flushAnswerOncePerFrame();
      setIsLoading(false);
      isStoppedRef.current = false;
      setIsStopped(false);
    };

    const scheduleReconnect = () => {
      if (!reconnectEnabled || hasFinished || isStoppedRef.current) return;
      if (!activeAssistantMessageId) {
        console.warn("Reconnect skipped because no active assistant message_id is available yet.");
        stopAll();
        return;
      }
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error("Max reconnect attempts reached.");
        stopAll();
        return;
      }
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
      reconnectAttempts += 1;
      setTimeout(() => {
        void connectWS(true).catch((error) => {
          console.error("WebSocket reconnect auth/setup error:", error);
          if (!isStoppedRef.current) {
            scheduleReconnect();
          }
        });
      }, delay);
    };

    const connectWS = async (isReconnect = false) => {
      if (!reconnectEnabled || hasFinished || isStoppedRef.current) return;

      const wsUrl = await getAuthenticatedWebSocketUrl(
        chat_id,
        user_id,
        isReconnect ? activeAssistantMessageId : undefined
      );
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isReconnect) {
          resetReplayState();
        }
        if (!isReconnect) {
          ws.send(JSON.stringify(requestBody));
        }
      };

      ws.onmessage = (event) => {
        if (isStoppedRef.current) {
          stopAll();
          return;
        }

        try {
          const data = JSON.parse(event.data);
          activeAssistantMessageId =
            data?.message_id ||
            (data.type === "MESSAGE_ID" && data.message != null
              ? String(data.message)
              : activeAssistantMessageId);

          // Handle messages
          if (data.message) {
            if (data.type === "YAHOO_FINANCE_TICKERS") {
                // if (
                //   data.message !== "False" &&
                //   !tabs.find((tab) => tab.tvSymbol === data.message)
                // ) {
                //   setTabs((tabs) => [
                //     ...tabs,
                //     {
                //       id: data.message,
                //       label: data.message,
                //       tvSymbol: data.message,
                //     },
                //   ]);
                //   // setTvTickers((tickers) => [data.message, ...tickers]);
                // }

                return;
              }

            if (data.type === "SUMMARY_UPDATE") {
              const summaryPayload =
                typeof data.message === "string"
                  ? JSON.parse(data.message)
                  : data.message;
              if (typeof setSummary === "function") {
                setSummary(summaryPayload?.summary || "");
              }
              if (typeof setSummaryVersion === "function") {
                setSummaryVersion(summaryPayload?.version || 0);
              }
              return;
            }

            // Types that render nothing inline must not break the open
            // collapsible: "urls" is kept as data and shown under the last
            // analysis-steps collapsible.
            const isSilentType =
              data.type === "SUGGESTIONS" ||
              data.type === "MESSAGE_ID" ||
              data.type === "urls";

            if (lastType === "CHART" && data.type !== "CHART") {
              addToBurst("</chartrender>");
            }
            if (collapsibleOpen && data.type !== "INFO" && !isSilentType) {
              addToBurst("</CollapsibleDiv>");
              collapsibleOpen = false;
            }
            if (data.type === "INFO" && !collapsibleOpen) {
              addToBurst("<CollapsibleDiv>");
              collapsibleOpen = true;
            }
            if (data.type === "CHART" && lastType !== "CHART") {
              addToBurst("<chartrender>");
            }

            if (data.type === "urls") {
              hasRenderableContent = true;
              addSourceUrls(data.message);
            } else if (data.type === "SUGGESTIONS") {
              setDynamicSuggestions(getChatSuggestionsFromPayload(data.message));
            } else if (data.type !== "MESSAGE_ID") {
              if (
                typeof data.message === "string" &&
                data.message.trim().length > 0
              ) {
                hasRenderableContent = true;
              }
              // Each INFO frame is one analysis step; the newline is what the
              // renderer splits rows on, so guarantee one per step.
              addToBurst(
                data.type === "INFO" &&
                  typeof data.message === "string" &&
                  !/\n\s*$/.test(data.message)
                  ? `${data.message}\n`
                  : data.message
              );
            }

            lastType = data.type || null;
          }

          // Store message_id on the last answer (no re-render storm)
          if (data.type === "MESSAGE_ID") {
            setMessages((prev) => {
              const updated = [...prev];
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i]?.type === "answer") {
                  updated[i] = { ...updated[i], message_id: data.message };
                  break;
                }
              }
              return updated;
            });
          }

          if (data.type === "FINISH") {
            hasFinished = true;
            reconnectEnabled = false;
            activeAssistantMessageId = null;
            closeCollapsibleIfOpen();
            // flush any pending burst and frame immediately
            if (wsBurstTimer) {
              clearTimeout(wsBurstTimer);
              wsBurstTimer = null;
              enqueueText(wsBurstBuf);
              wsBurstBuf = "";
            }
            flushAnswerOncePerFrame();
            if (!hasRenderableContent) {
              if (retryCount < EMPTY_RESPONSE_RETRY_LIMIT) {
                console.warn(
                  "EM GPT received an empty assistant response; retrying once."
                );
                try {
                  ws.close();
                } catch {}
                window.setTimeout(() => {
                  void askQuestion(
                    setIsLoading,
                    userInput,
                    setUserInput,
                    documentIdInput,
                    inUseDocumentIds,
                    setMessages,
                    messages,
                    setPageNumbers,
                    getPageNumber,
                    dashboardName,
                    filterCountries,
                    filterCompanies,
                    reference_ids,
                    searchFlag,
                    isStoppedRef,
                    setIsStopped,
                    sources,
                    default_embedding,
                    setDynamicSuggestions,
                    showSuggestions,
                    wsRef,
                    selectedNews,
                    newsList,
                    setIsStreaming,
                    ragRatioReq,
                    selectedPoint,
                    selectedDate,
                    pointClickNewsLookbackDays,
                    summary,
                    summaryVersion,
                    setSummary,
                    setSummaryVersion,
                    {
                      silentRetry: true,
                      retryCount: retryCount + 1,
                      preservedContext: lastQAContext,
                      userInputOverride: newMessage.text,
                      evidenceRefs: normalizedEvidenceRefs,
                      requestMode,
                      ticker,
                      slug,
                    }
                  );
                }, 0);
                return;
              }

              applyEmptyResponseFallback();
            }
            try {
              ws.close();
            } catch {}
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.onclose = () => {
        // Reconnect ONLY if not finished AND not stopped AND still enabled
        if (!hasFinished && !isStoppedRef.current && reconnectEnabled) {
          scheduleReconnect();
        } else {
          // ensure clean flags
          isStoppedRef.current = false;
          setIsStopped(false);
        }
      };
    };

    // Initial connect
    await connectWS(false);
  } catch (error) {
    console.error("askQuestion error:", error);
    setIsLoading(false);
  }
};

export const askQuestioReportReader = async (
  setIsLoading,
  userInput,
  setUserInput,
  documentIdInput,
  inUseDocumentIds,
  setMessages,
  messages,
  setPageNumbers,
  getPageNumber,
  evidenceRefs = []
) => {
  if (!userInput.trim()) return;
  var document_ids = [];
  if (documentIdInput.trim()) {
    document_ids = [documentIdInput]; // Use the provided documentId
  } else {
    document_ids = inUseDocumentIds; // Fetch documentIds if not provided
  }
  setIsLoading(true);
  setUserInput("");
  const newMessage = { type: "question", text: userInput };
  setMessages([...messages, newMessage]);
  try {
    const document_id = documentIdInput
      ? [documentIdInput]
      : document_ids.map((x) => x.doc_id);
    const query = newMessage.text;
    const boolGetPageNumber = getPageNumber ? "true" : "false";
    const lastQuestionAndAnswer = messages
      .filter(
        (message) => message.type === "question" || message.type === "answer"
      )
      .slice(-4);

    let contextString = lastQuestionAndAnswer
      .map((msg) => {
        return `${msg.type === "question" ? "question: " : "Answer: "} ${
          msg.text
        }`;
      })
      .join(" ");

    const requestBody = {
      query: `question: ${query}`,
      context: contextString,
      document_id,
      evidence_refs: Array.isArray(evidenceRefs) ? evidenceRefs : [],
    };
    if (document_id.length > 0) {
      const response = await fetchPutResponse(
        `conversation:query?page_numbers=${boolGetPageNumber}`,
        requestBody
      );
      const data = await response.json();
      if (!response.ok) {
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            { type: "answer", text: data.detail },
          ]);
        }, 1000);
        setIsLoading(false);
        throw new Error(`Error: ${response.status}`);
      } else {
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              type: "answer",
              text: data[0],
              source_url: data[1][0]["relevant_urls"],
            },
          ]);
        }, 1000);
        setIsLoading(false);
        setUserInput("");
      }
      if (getPageNumber) {
        const pageNumbersArray = data[1][0]["page_numbers_used"];
        const seen = new Set();
        const uniquePageNumbers = pageNumbersArray.filter((item) => {
          return seen.has(item) ? false : seen.add(item);
        });

        setPageNumbers(uniquePageNumbers);
      }
    } else {
      setMessages((prev) => [
        ...prev,
        {
          type: "answer",
          text: "No Context Available. Please modify filters and dates to continue.",
        },
      ]);
      setIsLoading(false);
    }
  } catch (error) {}
};

export const giveFeedback = async ({
  msgId,
  feedback,
  feedbackText,
  setFeedbackMessageId,
}) => {
  const requestBody = {
    feedback_text: feedbackText,
    message_id: msgId,
    feedback_type: feedback,
  };

  const response = await fetchPutResponse(`ws/feedback`, requestBody);
  const data = await response.json();
  if (!response.ok) {
  } else {
    setFeedbackMessageId((prev) => [...prev, msgId]);
  }
};
